import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calculator, TrendingDown, AlertCircle, CheckCircle2 } from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { EarlyAccessForm } from '@/components/EarlyAccessForm';
import { FOUNDER, founderPersonLd } from '@/lib/founder';

type Version = '11g' | '12c' | '14c';
type Deployment = 'on-prem' | 'oci' | 'hybrid';

interface Inputs {
  invoiceVolume: number;
  apTeamSize: number;
  version: Version;
  hasFipsa: boolean;
  deployment: Deployment;
  modernizationConsultingDayRate: number;
}

interface PathTco {
  path: string;
  oneTime: number;
  recurringYear1: number;
  recurringYear5Total: number;
  fiveYearTotal: number;
  rationale: string;
}

function formatCurrency(n: number): string {
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

// All numbers are illustrative ranges based on public Oracle documentation
// and typical Oracle Partner Network rate sheets. Not a vendor quote.
function calculate(inputs: Inputs): PathTco[] {
  const { invoiceVolume, apTeamSize, version, hasFipsa, deployment, modernizationConsultingDayRate: dayRate } = inputs;

  const complexityFactor = (hasFipsa ? 1.3 : 1.0) * (version === '11g' ? 1.2 : 1.0);
  const volumeFactor = Math.max(1, Math.log10(invoiceVolume / 1000));

  // PATH A — Lift-and-shift to OCI Marketplace.
  // Migration ~30-60 days of consulting + OCI infra + ongoing license + post-2027 Market Driven Support
  const pathAMigrationDays = 40 * complexityFactor;
  const pathAOneTime = pathAMigrationDays * dayRate;
  const pathAOciInfraMonthly = (deployment === 'on-prem' ? 1500 : 1000) * volumeFactor;
  const pathALicenseMaintAnnual = 35000 * complexityFactor;
  const pathAMarketDrivenAnnualPost2027 = 60000 * complexityFactor; // years 4-5
  const pathARecurringYear1 = pathAOciInfraMonthly * 12 + pathALicenseMaintAnnual;
  const pathARecurringYears2to3 = pathARecurringYear1 * 2; // years 2 + 3 under extended support
  const pathARecurringYears4to5 = (pathAOciInfraMonthly * 12 + pathAMarketDrivenAnnualPost2027) * 2;
  const pathAFiveYearTotal = pathAOneTime + pathARecurringYear1 + pathARecurringYears2to3 + pathARecurringYears4to5;

  // PATH A+ — Upgrade in place to 14c.
  // Larger upgrade effort, includes app re-test, customizations review.
  const pathAPlusUpgradeDays = 80 * complexityFactor;
  const pathAPlusOneTime = pathAPlusUpgradeDays * dayRate;
  const pathAPlusLicenseMaintAnnual = 38000 * complexityFactor;
  const pathAPlusInfraAnnual = (deployment === 'on-prem' ? 1500 : 1000) * 12 * volumeFactor;
  const pathAPlusRecurringYear1 = pathAPlusLicenseMaintAnnual + pathAPlusInfraAnnual;
  const pathAPlus5YearRecurring = pathAPlusRecurringYear1 * 5; // 14c stays in Premier Support
  const pathAPlusFiveYearTotal = pathAPlusOneTime + pathAPlus5YearRecurring;

  // PATH B — Modernize off WebCenter to EZ Cloud platform.
  // Implementation + ongoing SaaS. Eliminates WebCenter license + WebCenter infra.
  const pathBImplementationDays = 60 * complexityFactor;
  const pathBOneTime = pathBImplementationDays * dayRate;
  // EZ Cloud annual SaaS — placeholder range. Pricing established on Decision Call.
  const ezCloudAnnualEstimate = Math.max(40000, Math.min(120000, 35000 + invoiceVolume * 0.45));
  const pathBRecurringYear1 = ezCloudAnnualEstimate;
  const pathBFiveYearTotal = pathBOneTime + pathBRecurringYear1 * 5;

  // PATH B+ — Hybrid (Path B for AP, Path A for residual WebCenter Content).
  // Apply 60% of Path B + 40% of Path A.
  const pathBPlusOneTime = pathBOneTime * 0.7 + pathAOneTime * 0.4;
  const pathBPlusRecurringYear1 = pathBRecurringYear1 * 0.7 + (pathARecurringYear1) * 0.4;
  const pathBPlusFiveYearTotal = pathBPlusOneTime + pathBPlusRecurringYear1 * 5;

  return [
    {
      path: 'Path A — Lift-and-shift to OCI Marketplace',
      oneTime: pathAOneTime,
      recurringYear1: pathARecurringYear1,
      recurringYear5Total:
        pathARecurringYear1 + pathARecurringYears2to3 + pathARecurringYears4to5,
      fiveYearTotal: pathAFiveYearTotal,
      rationale:
        'Migration to OCI Marketplace, ongoing WebCenter license, infra, Extended Support through 2027, Market Driven Support 2028+.',
    },
    {
      path: 'Path A+ — Upgrade in place to 14c',
      oneTime: pathAPlusOneTime,
      recurringYear1: pathAPlusRecurringYear1,
      recurringYear5Total: pathAPlus5YearRecurring,
      fiveYearTotal: pathAPlusFiveYearTotal,
      rationale:
        'Upgrade effort + WebCenter 14c license through Premier Support window (8 years per Oracle). Higher one-time, lower steady-state.',
    },
    {
      path: 'Path B — Modernize off WebCenter (EZ Cloud)',
      oneTime: pathBOneTime,
      recurringYear1: pathBRecurringYear1,
      recurringYear5Total: pathBRecurringYear1 * 5,
      fiveYearTotal: pathBFiveYearTotal,
      rationale:
        'Implementation + EZ Cloud SaaS. No WebCenter license, no WebCenter infra. EZ Cloud pricing established on a Decision Call.',
    },
    {
      path: 'Path B+ — Hybrid (modernize AP, preserve WebCenter Content)',
      oneTime: pathBPlusOneTime,
      recurringYear1: pathBPlusRecurringYear1,
      recurringYear5Total: pathBPlusRecurringYear1 * 5,
      fiveYearTotal: pathBPlusFiveYearTotal,
      rationale:
        'Partial Path B (AP layer) + partial Path A (residual WebCenter Content). Fit for customers whose WCC usage extends beyond AP.',
    },
  ];
}

const WebCenterTcoCalculator = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [inputs, setInputs] = useState<Inputs>({
    invoiceVolume: 60000,
    apTeamSize: 6,
    version: '12c',
    hasFipsa: false,
    deployment: 'on-prem',
    modernizationConsultingDayRate: 2500,
  });

  const results = useMemo(() => calculate(inputs), [inputs]);
  const cheapest = useMemo(
    () => results.reduce((a, b) => (a.fiveYearTotal < b.fiveYearTotal ? a : b)),
    [results],
  );

  const personSchema = { '@context': 'https://schema.org', ...founderPersonLd };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="WebCenter AP December 2026 TCO Calculator | EZ Cloud"
        description="Free interactive total cost of ownership calculator for Oracle WebCenter AP customers facing the December 2026 Fusion Middleware 12c support deadline. Compare 5-year TCO across Path A (lift-and-shift to OCI Marketplace), Path A+ (12c → 14c upgrade), Path B (modernize off WebCenter), and Path B+ (hybrid)."
        canonical="https://ezcloud.ai/resources/webcenter-tco-calculator"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Resources', url: 'https://ezcloud.ai/resources/webcenter-tco-calculator' },
          {
            name: 'WebCenter TCO Calculator',
            url: 'https://ezcloud.ai/resources/webcenter-tco-calculator',
          },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(personSchema) }}
      />
      <Navigation />
      <Breadcrumbs />

      <main>
        {/* Hero */}
        <section className="bg-hero-gradient text-white pt-16 pb-16 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-primary/20 to-transparent" />
            <div className="absolute top-20 right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
          </div>
          <div className="container mx-auto px-4 max-w-5xl relative">
            <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
              <Calculator className="w-4 h-4" aria-hidden />
              Free Tool · TCO Calculator
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              The WebCenter AP
              <span className="block text-accent mt-3">December 2026 TCO Calculator</span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 max-w-3xl">
              Compare the 5-year total cost of ownership across the four forward paths for your
              specific WebCenter AP implementation.
            </p>
          </div>
        </section>

        {/* Inputs */}
        <section className="py-12 bg-white border-b border-gray-100">
          <div className="container mx-auto px-4 max-w-5xl">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">Your situation</h2>
            <Card className="p-7 bg-white border border-gray-200 shadow-sm">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="vol" className="text-gray-900">
                    Annual invoice volume
                  </Label>
                  <Input
                    id="vol"
                    type="number"
                    min={10000}
                    step={5000}
                    value={inputs.invoiceVolume}
                    onChange={(e) =>
                      setInputs({ ...inputs, invoiceVolume: Math.max(10000, +e.target.value) })
                    }
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Total AP invoices processed per year.
                  </p>
                </div>
                <div>
                  <Label htmlFor="team" className="text-gray-900">
                    AP team size (FTE)
                  </Label>
                  <Input
                    id="team"
                    type="number"
                    min={1}
                    value={inputs.apTeamSize}
                    onChange={(e) =>
                      setInputs({ ...inputs, apTeamSize: Math.max(1, +e.target.value) })
                    }
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-900">Current WebCenter version</Label>
                  <Select
                    value={inputs.version}
                    onValueChange={(v) => setInputs({ ...inputs, version: v as Version })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="11g">WebCenter 11g</SelectItem>
                      <SelectItem value="12c">WebCenter 12c</SelectItem>
                      <SelectItem value="14c">WebCenter 14c</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-900">Deployment today</Label>
                  <Select
                    value={inputs.deployment}
                    onValueChange={(v) => setInputs({ ...inputs, deployment: v as Deployment })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="on-prem">On-premises</SelectItem>
                      <SelectItem value="oci">Oracle Cloud Infrastructure</SelectItem>
                      <SelectItem value="hybrid">Hybrid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-900">FIPSA implementation?</Label>
                  <Select
                    value={inputs.hasFipsa ? 'yes' : 'no'}
                    onValueChange={(v) => setInputs({ ...inputs, hasFipsa: v === 'yes' })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="no">No</SelectItem>
                      <SelectItem value="yes">Yes — uses FIPSA accelerator</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="rate" className="text-gray-900">
                    Oracle consulting day rate
                  </Label>
                  <Input
                    id="rate"
                    type="number"
                    min={1000}
                    step={100}
                    value={inputs.modernizationConsultingDayRate}
                    onChange={(e) =>
                      setInputs({
                        ...inputs,
                        modernizationConsultingDayRate: Math.max(1000, +e.target.value),
                      })
                    }
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    USD/day. Default $2,500 reflects typical Oracle Partner rates.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </section>

        {/* Results */}
        <section className="py-12 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">5-year TCO</h2>
            <Card className="p-7 bg-white border border-gray-200 shadow-md mb-6">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-gray-200">
                    <tr>
                      <th className="text-left text-xs font-bold uppercase tracking-wide text-accent py-3 px-3">
                        Path
                      </th>
                      <th className="text-right text-xs font-bold uppercase tracking-wide text-accent py-3 px-3">
                        One-time
                      </th>
                      <th className="text-right text-xs font-bold uppercase tracking-wide text-accent py-3 px-3">
                        Recurring (Year 1)
                      </th>
                      <th className="text-right text-xs font-bold uppercase tracking-wide text-accent py-3 px-3">
                        5-year total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((r) => (
                      <tr
                        key={r.path}
                        className={`border-b border-gray-200 last:border-b-0 ${
                          r.path === cheapest.path ? 'bg-accent/5' : ''
                        }`}
                      >
                        <td className="py-3 px-3 font-semibold text-gray-900">
                          {r.path}
                          {r.path === cheapest.path && (
                            <span className="ml-2 text-xs bg-accent text-white px-2 py-0.5 rounded-full">
                              Lowest TCO
                            </span>
                          )}
                          <p className="text-xs text-gray-500 mt-1 font-normal">{r.rationale}</p>
                        </td>
                        <td className="py-3 px-3 text-right text-gray-700">
                          {formatCurrency(r.oneTime)}
                        </td>
                        <td className="py-3 px-3 text-right text-gray-700">
                          {formatCurrency(r.recurringYear1)}
                        </td>
                        <td className="py-3 px-3 text-right text-gray-900 font-bold">
                          {formatCurrency(r.fiveYearTotal)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-6 pt-5 border-t border-gray-200 flex items-start gap-3 text-sm text-gray-600">
                <AlertCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" aria-hidden />
                <p>
                  Numbers are illustrative ranges based on public Oracle documentation and typical
                  Oracle Partner Network rate sheets. Not a vendor quote. EZ Cloud platform
                  pricing is established per-engagement on a Decision Call.
                </p>
              </div>
            </Card>
          </div>
        </section>

        {/* Save / share */}
        <section className="py-12 bg-white">
          <div className="container mx-auto px-4 max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Save these results
            </p>
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-4 leading-tight">
              Get a printable PDF of your TCO comparison
            </h2>
            <p className="text-center text-gray-600 mb-8 max-w-2xl mx-auto">
              We'll email you a printable PDF report including your inputs, the 5-year TCO
              comparison, and the assumptions behind each number. Useful for sharing with internal
              stakeholders, procurement, or your Oracle account team.
            </p>
            <EarlyAccessForm
              tool="WebCenter TCO Calculator"
              source="tco-calc"
              eventName="webcenter_tco_calculator_request"
              buttonLabel="Email me the printable report"
              successHeadline="Request received — printable report on the way"
              successBody={`${FOUNDER.name} will personally email you the printable TCO report within 1 business day, along with an offer to walk through the assumptions on a Decision Call.`}
            />
          </div>
        </section>

        {/* Methodology */}
        <section className="py-12 bg-gray-50 border-y border-gray-200">
          <div className="container mx-auto px-4 max-w-3xl">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
              How we calculate
            </h2>
            <div className="space-y-5 text-gray-700 leading-relaxed">
              <div>
                <h3 className="font-bold text-gray-900 mb-2">Path A — Lift-and-shift to OCI Marketplace</h3>
                <p className="text-sm">
                  Migration consulting ({Math.round(40 * (inputs.hasFipsa ? 1.3 : 1.0) * (inputs.version === '11g' ? 1.2 : 1.0))} days at ${inputs.modernizationConsultingDayRate}/day) +
                  OCI infra (${Math.round((inputs.deployment === 'on-prem' ? 1500 : 1000) * Math.max(1, Math.log10(inputs.invoiceVolume / 1000)))}/month) +
                  WebCenter license maintenance ($35K-45K annual) + Extended Support through 2027, then Market Driven Support yearly thereafter.
                </p>
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-2">Path A+ — Upgrade in place to 14c</h3>
                <p className="text-sm">
                  Larger upgrade effort (~80 days adjusted for complexity) + ongoing WebCenter 14c license through Premier Support window (8 years per Oracle).
                  Higher one-time, lower steady-state.
                </p>
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-2">Path B — Modernize off WebCenter</h3>
                <p className="text-sm">
                  Implementation effort + ongoing EZ Cloud SaaS. WebCenter license + WebCenter infra eliminated. EZ Cloud pricing is established per-engagement on a Decision Call; calculator uses an illustrative range based on volume.
                </p>
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-2">Path B+ — Hybrid</h3>
                <p className="text-sm">
                  Partial Path B (AP layer) + partial Path A (residual WebCenter Content). For customers whose WCC usage extends beyond AP.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-hero-gradient text-white">
          <div className="container mx-auto px-4 max-w-3xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Want to talk through your specific numbers?
            </h2>
            <p className="text-xl text-white/80 mb-8 leading-relaxed">
              A 30-minute Decision Call with {FOUNDER.name} to walk through the assumptions in
              your context and discuss what each path actually looks like for your implementation.
            </p>
            <Link
              to="/solutions/modernize-oracle-webcenter"
              className="inline-flex items-center font-semibold text-white bg-accent hover:bg-accent/90 transition-colors px-6 py-3 rounded-lg"
            >
              See the full WebCenter modernization path
              <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
};

export default WebCenterTcoCalculator;
